import React, { useEffect, useState } from "react";
import api, { withCeoPassword } from "@/lib/api";
import { Eye, Printer, Trash2, Inbox, Search } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import BulkDeleteBar from "@/components/BulkDeleteBar";
import TableExportButton from "@/components/TableExportButton";

const STATUS_STYLES = {
  open: "bg-emerald-100 text-emerald-700",
  balance: "bg-amber-100 text-amber-700",
  closed: "bg-slate-200 text-slate-700",
};

const PG_LABEL = {
  direct_purchase: "Direct Purchase",
  indirect_purchase: "Indirect Purchase",
  labour_work: "Labour Work",
};

export default function POLog() {
  const { user } = useAuth();
  const canManage = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [view, setView] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);
  const navigate = useNavigate();

  const load = () => {
    api.get("/purchase-orders").then((r) => setRows(r.data));
    setSelected([]);
  };

  useEffect(() => { load(); }, []);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = (ids) =>
    setSelected((s) => (ids.every((id) => s.includes(id)) ? s.filter((x) => !ids.includes(x)) : Array.from(new Set([...s, ...ids]))));

  const del = async (po) => {
    if (!window.confirm(`Delete ${po.po_number}? If GRN/store data exists, you'll be prompted for the CEO password.`)) return;
    try {
      await withCeoPassword((cfg) => api.delete(`/purchase-orders/${po.id}`, cfg));
      toast.success(`${po.po_number} deleted`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = rows.filter((po) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (
      (po.po_number || "").toLowerCase().includes(s) ||
      (po.indent_no || "").toLowerCase().includes(s) ||
      (po.supplier?.name || "").toLowerCase().includes(s)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">
            Module · Procurement
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
            PO Log
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            All generated Purchase Orders. Status closes automatically once an Issue Slip is generated.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="po-log-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by PO number, indent number or supplier…"
              className="pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm w-80 focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951]"
            />
          </div>
          <TableExportButton
            testIdPrefix="po-export"
            filename="po_log"
            rows={filtered.map((po) => ({
              po_number: po.po_number,
              po_date: (po.po_date || po.created_at || "").slice(0, 10),
              indent_no: po.indent_no || "",
              supplier: po.supplier?.name || "",
              status: po.status || "",
              purchase_group: PG_LABEL[po.purchase_group] || po.purchase_group || "",
              company: po.company_name || "",
              plant: po.plant_name || "",
              total_value: (po.items || []).reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0),
            }))}
            dateField="po_date"
            columns={[
              { key: "po_number", label: "PO No" },
              { key: "po_date", label: "PO Date" },
              { key: "indent_no", label: "Indent No" },
              { key: "supplier", label: "Supplier" },
              { key: "purchase_group", label: "Purchase Group" },
              { key: "company", label: "Company" },
              { key: "plant", label: "Plant" },
              { key: "status", label: "Status" },
              { key: "total_value", label: "Total Value" },
            ]}
          />
        </div>
      </div>

      <BulkDeleteBar
        resource="purchase-orders"
        selectedIds={selected}
        onClear={() => setSelected([])}
        onDeleted={load}
        testid="po-bulk"
      />

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                {canManage && (
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      data-testid="po-select-all"
                      checked={filtered.length > 0 && filtered.every((p) => selected.includes(p.id))}
                      onChange={() => toggleAll(filtered.map((p) => p.id))}
                    />
                  </th>
                )}
                <th className="text-left px-6 py-3">PO No.</th>
                <th className="text-left px-6 py-3">Indent No.</th>
                <th className="text-left px-6 py-3">Date</th>
                <th className="text-left px-6 py-3">Supplier</th>
                <th className="text-left px-6 py-3">Purchase Group</th>
                <th className="text-right px-6 py-3">Items</th>
                <th className="text-right px-6 py-3">Grand Total</th>
                <th className="text-left px-6 py-3">Status</th>
                <th className="text-right px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="po-log-list">
              {filtered.length === 0 && (
                <tr><td colSpan={canManage ? 10 : 9} className="text-center py-16 text-slate-400">
                  <Inbox size={28} className="mx-auto mb-2 opacity-40" />
                  {rows.length === 0 ? "No purchase orders yet" : "No matches for your search"}
                </td></tr>
              )}
              {filtered.map((po) => {
                const status = po.status || "open";
                const pg = po.purchase_group;
                return (
                  <tr key={po.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`po-log-row-${po.po_number}`}>
                    {canManage && (
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.includes(po.id)}
                          onChange={() => toggle(po.id)}
                          data-testid={`po-select-${po.po_number}`}
                        />
                      </td>
                    )}
                    <td className="px-6 py-3 font-mono font-semibold">{po.po_number}</td>
                    <td className="px-6 py-3 font-mono text-xs text-slate-700" data-testid={`po-indent-no-${po.po_number}`}>{po.indent_no || "—"}</td>
                    <td className="px-6 py-3 font-mono text-xs">{po.date}</td>
                    <td className="px-6 py-3">{po.supplier?.name}</td>
                    <td className="px-6 py-3">
                      {pg ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-[#1D2951]/10 text-[#1D2951]">
                          {PG_LABEL[pg] || pg}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-6 py-3 text-right font-mono">{po.items.length}</td>
                    <td className="px-6 py-3 text-right font-mono font-semibold">₹{po.grand_total.toLocaleString("en-IN")}</td>
                    <td className="px-6 py-3">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold ${STATUS_STYLES[status] || "bg-slate-100 text-slate-600"}`}>
                        {status}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => setView(po)} className="p-1.5 hover:bg-slate-100 rounded text-slate-600" title="View" data-testid={`view-po-${po.po_number}`}>
                          <Eye size={14} />
                        </button>
                        <button onClick={() => navigate(`/po/print/${po.id}`)} className="p-1.5 hover:bg-slate-100 rounded text-[#1D2951]" title="Print" data-testid={`print-po-${po.po_number}`}>
                          <Printer size={14} />
                        </button>
                        {canManage && (
                          <button onClick={() => del(po)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600" title="Delete (admin)" data-testid={`delete-po-${po.po_number}`}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{view?.po_number} — Preview</DialogTitle>
            <DialogDescription>Summary of the generated Purchase Order.</DialogDescription>
          </DialogHeader>
          {view && (
            <div className="text-sm space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <KV k="Supplier" v={view.supplier?.name} />
                <KV k="Date" v={view.date} />
                <KV k="Payment Terms" v={view.payment_terms} />
                <KV k="Validity" v={view.validity} />
                <KV k="Purchase Group" v={PG_LABEL[view.purchase_group] || view.purchase_group || "—"} />
                {view.process && <KV k="Process" v={view.process} />}
              </div>
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase">
                    <tr>
                      <th className="px-3 py-2 text-left">Item</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-right">Price</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.items.map((it, idx) => (
                      <tr key={idx} className="border-t border-slate-100">
                        <td className="px-3 py-2">{it.description}</td>
                        <td className="px-3 py-2 text-right font-mono">{it.quantity} {it.unit}</td>
                        <td className="px-3 py-2 text-right font-mono">₹{it.price}</td>
                        <td className="px-3 py-2 text-right font-mono">₹{it.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-right text-sm space-y-0.5">
                <div>Subtotal: <span className="font-mono">₹{view.subtotal}</span></div>
                <div>IGST ({view.igst_rate}%): <span className="font-mono">₹{view.igst_amount}</span></div>
                <div className="font-semibold text-base">Grand Total: <span className="font-mono">₹{view.grand_total}</span></div>
                <div className="text-xs text-slate-500 italic">{view.amount_in_words}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const KV = ({ k, v }) => (<div><div className="text-[10px] uppercase tracking-widest text-slate-500">{k}</div><div className="text-sm text-slate-900 font-medium">{v || "—"}</div></div>);
